import { useSize } from '@cibo/ui/src/hooks/useSize'
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import * as d3 from 'd3'
import { ascend, groupBy, max, pluck, prop, sum, uniq, uniqBy } from 'ramda'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'
import { Swatch } from '../../../../consts/Theme'
import { InventoryCsvRow } from '../../types'

type CarbonIntensityBySourceProps = {
  height: number
  csvData: Array<InventoryCsvRow>
  commodity: string
}

const PADDING = {
  left: 56,
  top: 16,
  right: 64,
  bottom: 32,
}

export const CarbonIntensityBySource = ({
  height,
  csvData,
  commodity,
}: CarbonIntensityBySourceProps) => {
  const { t } = useTranslation('impact/CarbonIntensityBySource')
  const theme = useTheme()

  const graphId = useMemo(() => `graph-${uuid()}`, [])
  const sizeTarget = useRef<HTMLElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  const size = useSize(sizeTarget, 100)

  const { width } = size || { width: 0 }

  const innerWidth = width ? width - PADDING.left - PADDING.right : 0
  const innerHeight = height - PADDING.bottom - PADDING.top

  const commodityRows: InventoryCsvRow[] = csvData?.filter(
    d => d.emissionsCategory !== 'Removals' && d.commodity === commodity
  )

  const allYears: number[] = uniq(pluck('year')(commodityRows))

  const colorScale = d3.scaleOrdinal(d3.schemePaired)

  const emissionSources: string[] = []

  const rowsBySource = groupBy(prop('emissionsSource'), commodityRows)
  for (const [source, rows] of Object.entries(rowsBySource)) {
    const total = sum(pluck('kgCO2EquivPerKgTotalYield', rows))

    if (total > 0) emissionSources.push(source)
  }

  const csvStacks = commodityRows.reduce((acc, item) => {
    if (!acc[item.year]) {
      acc[item.year] = { year: item.year }
    }

    if (typeof item['kgCO2EquivPerKgTotalYield'] === 'number') {
      acc[item.year][item['emissionsSource']] = item['kgCO2EquivPerKgTotalYield']
    }

    return acc
  }, {} as Record<number, Record<string, number>>)

  const stacks = d3.stack().keys(emissionSources)(Object.values(csvStacks))

  const yieldRows = uniqBy(
    prop('year'),
    commodityRows.filter(({ yieldKgPerAcre }) => typeof yieldKgPerAcre === 'number')
  ).map(d => {
    const acres = Number(d.totalTillableManagedAcres)

    return {
      year: d.year,
      yield: acres * d.yieldKgPerAcre,
    }
  })

  yieldRows.sort(ascend(prop('year')))

  const xScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([(d3.min(allYears) || 0) - 0.5, (d3.max(allYears) || 0) + 0.5])
        .range([0, innerWidth]),
    [allYears]
  )

  const yScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([
          0,
          1.1 * stacks.length
            ? stacks[stacks.length - 1]
                ?.map((d: { 0: number; 1: number }) => (d[0] > d[1] ? d[0] : d[1]))
                .reduce(max)
            : 0,
        ])
        .range([innerHeight, 0]),
    [stacks]
  )

  const yieldScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([0, 1.1 * (d3.max(yieldRows?.map(d => d.yield)) || 0)])
        .range([innerHeight, 0]),
    [yieldRows]
  )

  const lineData = d3
    .line<{ year: number; yield: number }>()
    .x(d => xScale(d.year))
    .y(d => yieldScale(d.yield))

  useEffect(() => {
    const svg = d3.select(svgRef.current)

    svg
      .selectAll<SVGGElement, any>('g.x.axis')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickValues(allYears)
          .tickFormat(d => `${d}`)
          .tickSizeOuter(0)
          .tickSizeInner(0)
      )

    svg
      .selectAll<SVGGElement, any>('g.yield.axis')
      .call(
        d3
          .axisRight(yieldScale)
          .ticks(4)
          .tickSizeInner(0)
          .tickSizeOuter(0)
          .tickFormat(d => `${Number(d) / 1_000_000}`)
      )
      .attr('transform', `translate(${width - PADDING.left - PADDING.right},0)`)
      .call(g => g.select('.domain').remove())

    yScale &&
      svg
        .selectAll<SVGGElement, any>('g.y.axis')
        .call(
          d3
            .axisLeft(yScale)
            .ticks(4)
            .tickSizeInner(0)
        )
        .call(g => g.select('.domain').remove())
        .call(g => {
          g.selectAll('.grid').remove()
          g.selectAll('.tick line')
            .clone()
            .attr('x2', innerWidth)
            .attr('stroke-opacity', 0.1)
            .attr('class', 'grid')
        })
  }, [commodityRows, width, yieldRows, xScale, yScale])

  if (!commodityRows?.length) return null

  return (
    <Box
      ref={sizeTarget}
      sx={{
        'svg text': {
          fontSize: 12,
          fontWeight: 400,
          fill: theme.palette.text.secondary,
        },
        'g.x.axis text': {
          transform: `translateY(8px)`,
        },
        'g.axis path': {
          stroke: theme.palette.text.secondary,
          strokeWidth: 2,
          opacity: 0.3,
        },
      }}
    >
      <svg ref={svgRef} id={graphId} width={width || undefined} height={height}>
        <text
          textAnchor="middle"
          transform={` translate(20,${PADDING.top + (height - PADDING.bottom) / 2} ) rotate(-90)`}
        >
          {t('yAxisLabel')}
        </text>

        <text
          textAnchor="middle"
          transform={` translate(${width - 16},${PADDING.top +
            (height - PADDING.bottom) / 2} ) rotate(-90)`}
        >
          {t('totalAnnualYield', { commodity })}
        </text>

        <g transform={`translate(${PADDING.left},${PADDING.top})`}>
          <g className="y axis"></g>
          <g className="yield axis"></g>

          {stacks?.map(sourceStack =>
            sourceStack.map(d => (
              <Tooltip
                key={d.data[sourceStack.key]}
                placement="left"
                title={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Swatch color={colorScale(sourceStack.key)} />
                    <Typography variant="caption" color="primary.contrastText">
                      {`${sourceStack.key}: ${d.data[sourceStack.key].toPrecision(3)}`}
                    </Typography>
                  </Stack>
                }
              >
                <rect
                  x={xScale(d.data.year) - 12}
                  y={yScale(d[1])}
                  width={24}
                  height={yScale(d[0]) - yScale(d[1])}
                  fill={colorScale(sourceStack.key)}
                />
              </Tooltip>
            ))
          )}

          {yieldRows && (
            <path
              d={lineData(yieldRows) || undefined}
              fill="none"
              stroke={theme.palette.text.secondary}
              strokeWidth={1.5}
            />
          )}

          <g className="x axis"></g>
        </g>
      </svg>

      <Stack ml={2}>
        <Box>
          {Array.from(emissionSources).map(source => (
            <span key={source}>
              <div style={{ display: 'inline-block' }}>
                <Stack direction="row" alignItems="center" spacing={1} width={'content'} mr={2}>
                  <Swatch color={colorScale(source)} />
                  <Typography variant="caption">{source}</Typography>
                </Stack>
              </div>
            </span>
          ))}
        </Box>

        <Typography variant="caption" mt={2}>
          {t('footnote')}
        </Typography>
      </Stack>
    </Box>
  )
}
